import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { db, ilike } from "../../lib/api";
import { invalidate, useDebounced, useQuery } from "../../lib/hooks";
import { useRefData } from "../../lib/refdata";
import { useSession } from "../../lib/session";
import { navigate } from "../../lib/router";
import { PageHead } from "../../components/Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Select, fmtDate, money, useToast } from "../../components/ui";

/** Clients → Import solutions: a provider's list (Excel or CSV) is read on this computer and checked
 *  against the CRM. New solutions are added and statement values updated; anything that doesn't
 *  match waits under "To review" for a person to decide. The file itself is not stored. */

type Cell = string | number | boolean | Date | null;
const FIELDS: { key: string; label: string; hint?: string; guess: RegExp }[] = [
  { key: "client", label: "Client name", hint: "Full name, or “Last, First”", guess: /^(client|owner|policy ?owner|account ?holder|name|full ?name|investor|annuitant|applicant)( name)?$/i },
  { key: "client_first", label: "…or first name", guess: /^first( ?name)?$/i },
  { key: "client_last", label: "…and last name", guess: /^(last|sur)( ?name)?$/i },
  { key: "contract_no", label: "Contract / account number", guess: /(contract|policy|account|plan) ?(no|num|number|#|id)|^(contract|policy|account)$/i },
  { key: "solution_type", label: "Kind of solution", hint: "TFSA, RRSP, Term life…", guess: /^(plan ?type|account ?type|product ?type|registration|type|solution)$/i },
  { key: "product", label: "Product / plan name", guess: /^(product|plan|plan ?name|product ?name|fund|description)$/i },
  { key: "status", label: "Status", guess: /status/i },
  { key: "premium", label: "Premium ($)", guess: /premium|modal/i },
  { key: "premium_frequency", label: "Premium frequency", guess: /freq|mode/i },
  { key: "coverage", label: "Coverage amount ($)", guess: /face|coverage|benefit|death|sum insured|amount insured/i },
  { key: "market_value", label: "Market / cash value ($)", guess: /(market|account|cash|current|total) ?value|balance|valuation/i },
  { key: "as_of", label: "Value as of", guess: /as ?of|valuation ?date|statement ?date/i },
  { key: "effective_date", label: "Start / issue date", guess: /issue|effective|open(ed)?|start|inception/i },
];

function parseCsv(text: string): string[][] {
  const out: string[][] = []; let row: string[] = []; let cur = ""; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) { if (ch === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; continue; }
    if (ch === '"') q = true;
    else if (ch === ",") { row.push(cur); cur = ""; }
    else if (ch === "\n" || ch === "\r") { if (ch === "\r" && text[i + 1] === "\n") i++; row.push(cur); out.push(row); row = []; cur = ""; }
    else cur += ch;
  }
  if (cur || row.length) { row.push(cur); out.push(row); }
  return out.filter((r) => r.some((c) => c.trim() !== ""));
}
const num = (v: Cell) => { if (v == null || v === "") return ""; if (typeof v === "number") return String(v); const s = String(v).replace(/[$,\s]/g, "").replace(/^\((.*)\)$/, "-$1"); return isNaN(Number(s)) ? "" : s; };
const date = (v: Cell) => {
  if (v == null || v === "") return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);   // Excel dates are calendar days (no time zone)
  if (typeof v === "number" && v > 20000 && v < 80000) return dayjs("1899-12-30").add(v, "day").format("YYYY-MM-DD");   // Excel serial
  const d = dayjs(String(v)); return d.isValid() ? d.format("YYYY-MM-DD") : "";
};
/** Provider wording → the CRM's statuses */
const status = (v: string) => {
  const t = v.toLowerCase();
  if (!t) return "";
  if (/in ?force|^active|issued|^open|^current/.test(t)) return "Active";
  if (/paid.?up/.test(t)) return "Paid up";
  if (/laps/.test(t)) return "Lapsed";
  if (/underwrit|pending|submitted|in progress/.test(t)) return "In underwriting";
  if (/approved/.test(t)) return "Approved – not placed";
  if (/declin|not approved|postpon/.test(t)) return "Not approved";
  if (/transfer/.test(t)) return "Transferred out";
  if (/terminat|surrender|cancel|closed|matured|death|claim/.test(t)) return "Terminated";
  return v;
};
const freq = (v: string) => (/^m|month/i.test(v) ? "Monthly" : /^a|annual|year/i.test(v) ? "Annual" : /quarter/i.test(v) ? "Quarterly" : /semi|half/i.test(v) ? "Semi-annual" : v);
const text = (v: Cell) => (v == null ? "" : v instanceof Date ? dayjs(v).format("YYYY-MM-DD") : String(v).trim());

export default function ImportSolutions() {
  const { can } = useSession();
  const ref = useRefData();
  const toast = useToast();
  const [provider, setProvider] = useState("");
  const [full, setFull] = useState(false);
  const [file, setFile] = useState<string>("");
  const [sheets, setSheets] = useState<{ sheet: string; data: Cell[][] }[]>([]);
  const [sheetIx, setSheetIx] = useState(0);
  const [map, setMap] = useState<Record<string, number | undefined>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const [result, setResult] = useState<any>(null);
  const editable = can("clients", "edit");

  const data = sheets[sheetIx]?.data ?? [];
  const headerIx = Math.max(0, data.findIndex((r) => r.filter((c) => typeof c === "string" && c.trim()).length >= 2));
  const headers = (data[headerIx] ?? []).map((h) => text(h));
  const body = data.slice(headerIx + 1).filter((r) => r.some((c) => c != null && String(c).trim() !== ""));

  function autoMap(hs: string[]) {
    const m: Record<string, number | undefined> = {};
    for (const f of FIELDS) { const i = hs.findIndex((h, j) => f.guess.test(h) && !Object.values(m).includes(j)); if (i >= 0) m[f.key] = i; }
    setMap(m);
  }
  async function load(f: File) {
    setErr(null); setResult(null); setFile(f.name);
    try {
      let sh: { sheet: string; data: Cell[][] }[];
      if (/\.csv$/i.test(f.name)) sh = [{ sheet: "CSV", data: parseCsv(await f.text()) }];
      else {
        const mod: any = await import("read-excel-file/universal");
        sh = await (mod.default ?? mod.readXlsxFile)(await f.arrayBuffer());
      }
      if (!sh.length || !sh[0].data.length) throw new Error("That file looks empty.");
      setSheets(sh); setSheetIx(0);
      const d = sh[0].data; const hi = Math.max(0, d.findIndex((r) => r.filter((c) => typeof c === "string" && c.trim()).length >= 2));
      autoMap((d[hi] ?? []).map((h) => text(h)));
      const guess = (ref.data?.list("Providers") ?? []).find((p) => f.name.toLowerCase().includes(p.toLowerCase().split(" ")[0]));
      if (guess && !provider) setProvider(guess);
    } catch (e) { setErr(e as Error); setSheets([]); }
  }
  const rows = useMemo(() => body.map((r, i) => {
    const g = (k: string) => (map[k] == null ? null : r[map[k]!] ?? null);
    const client = text(g("client")) || [text(g("client_first")), text(g("client_last"))].filter(Boolean).join(" ");
    return {
      row: headerIx + 2 + i, client, contract_no: text(g("contract_no")), solution_type: text(g("solution_type")), product: text(g("product")),
      status: status(text(g("status"))), premium: num(g("premium")), premium_frequency: freq(text(g("premium_frequency"))), coverage: num(g("coverage")),
      market_value: num(g("market_value")), as_of: date(g("as_of")), effective_date: date(g("effective_date")),
    };
  }).filter((r) => r.client || r.contract_no), [body, map, headerIx]);

  async function run() {
    setErr(null); setBusy(true);
    try {
      if (!provider) throw new Error("Choose which provider this file is from.");
      if (map.client == null && map.client_first == null && map.contract_no == null) throw new Error("Match at least the client name (or contract number) column.");
      const r = await db.rpc("solutions_import", { p_provider: provider, p_file: file, p_full: full, p_rows: rows });
      setResult(r); invalidate("clients"); invalidate("import:"); toast("Import done");
    } catch (e) { setErr(e as Error); } finally { setBusy(false); }
  }

  if (!editable) return <Empty title="Only people who can edit clients can import">Ask the owner for access.</Empty>;
  return (
    <>
      <PageHead title="Import solutions" sub="Upload a provider's list of your clients' accounts and policies. The file is read on this computer and not stored. New ones are added, values updated, and anything that doesn't match is listed below for you to decide." />
      <ErrorBox error={err} />
      <Panel title="1 · The file">
        <div className="panel-body form-grid">
          <Field label="Provider (the file is from)"><Select value={provider} onChange={setProvider} options={ref.data?.list("Providers") ?? []} placeholder="Choose…" /></Field>
          <Field label="Excel (.xlsx) or CSV file"><input className="input" type="file" accept=".xlsx,.csv" onChange={(e) => e.target.files?.[0] && load(e.target.files[0])} /></Field>
          {sheets.length > 1 && <Field label="Sheet"><Select value={String(sheetIx)} onChange={(v) => { setSheetIx(Number(v)); const d = sheets[Number(v)].data; autoMap((d.find((r) => r.filter((c) => typeof c === "string").length >= 2) ?? []).map((h) => text(h))); }}
            options={sheets.map((s, i) => ({ value: String(i), label: s.sheet }))} /></Field>}
          <label className="switch-row wide"><input type="checkbox" checked={full} onChange={(e) => setFull(e.target.checked)} />
            <span>This is the provider's <b>full</b> list of my clients <span className="small muted">· then anything active in the CRM for {provider || "this provider"} that isn't in the file is listed to check</span></span></label>
        </div>
      </Panel>
      {headers.length > 0 && <>
        <Panel title="2 · Match the columns" hint={`${rows.length} rows found in ${file}. Guessed from the headings; change any that are wrong.`}>
          <div className="panel-body form-grid">
            {FIELDS.map((f) => (
              <Field key={f.key} label={f.label + (f.hint ? ` (${f.hint})` : "")}>
                <Select value={map[f.key] == null ? "" : String(map[f.key])} onChange={(v) => setMap({ ...map, [f.key]: v === "" ? undefined : Number(v) })}
                  options={headers.map((h, i) => ({ value: String(i), label: h || `Column ${i + 1}` }))} placeholder="— not in this file —" />
              </Field>
            ))}
          </div>
        </Panel>
        <Panel title="3 · Check, then import" hint="The first rows as they'll be read">
          <div className="table-wrap"><table className="data">
            <thead><tr><th>Client</th><th>Contract</th><th>Kind / product</th><th className="num">Premium</th><th className="num">Coverage</th><th className="num">Value</th><th>As of</th></tr></thead>
            <tbody>{rows.slice(0, 6).map((r) => <tr key={r.row}><td>{r.client || "—"}</td><td className="mono small">{r.contract_no || "—"}</td><td>{[r.solution_type, r.product].filter(Boolean).join(" · ") || "—"}</td>
              <td className="num">{r.premium ? money(Number(r.premium)) : "—"}</td><td className="num">{r.coverage ? money(Number(r.coverage)) : "—"}</td><td className="num">{r.market_value ? money(Number(r.market_value)) : "—"}</td><td>{r.as_of ? fmtDate(r.as_of) : "—"}</td></tr>)}</tbody>
          </table></div>
          <div className="panel-body toolbar" style={{ margin: 0 }}>
            <button className="btn primary" disabled={busy || !rows.length} onClick={run}>{busy ? "Importing…" : `Import ${rows.length} rows from ${provider || "…"}`}</button>
            {result && <span className="small"><b>{result.added}</b> added · <b>{result.values_updated}</b> values updated · <b>{result.same}</b> already the same · <b>{result.issues}</b> to review below</span>}
          </div>
        </Panel>
      </>}
      <ToReview />
      <PastImports />
    </>
  );
}

const KIND: Record<string, string> = {
  different: "The file and the CRM disagree", not_found: "No client with this name", more_than_one: "More than one person with this name",
  no_type: "Can't tell what kind of solution this is", missing: "In the CRM, but not in the provider's full list",
};
const FIELD_LABEL: Record<string, string> = { status: "Status", premium: "Premium", product: "Product", contract_no: "Contract no.", permanent_amount: "Permanent coverage", term_amount: "Term coverage", ci_amount: "CI coverage" };

/** Like Leads → Cleanup: each item waits for a person. */
function ToReview() {
  const q = useQuery("import:issues", () => db.select<any>("import_issues", { status: "eq.Open", select: "*,import_batches(provider,file_name,created_at),people(full_name),client_holdings(solution_type,provider,contract_no,status)", order: "created_at.desc" }));
  if (q.loading && !q.data) return <Panel title="To review"><Loading rows={3} /></Panel>;
  return (
    <Panel title={`To review (${q.data?.length ?? 0})`} hint="Nothing here changes until you choose. Every choice is noted in the client's history.">
      {!q.data?.length ? <div className="panel-body small muted">Nothing to review.</div>
        : <div className="stack panel-body">{q.data.map((i: any) => <IssueCard key={i.id} i={i} />)}</div>}
    </Panel>
  );
}

function IssueCard({ i }: { i: any }) {
  const toast = useToast();
  const ref = useRefData();
  const [err, setErr] = useState("");
  const [person, setPerson] = useState<{ id: string; full_name: string } | null>(null);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const pick = i.kind === "not_found" || i.kind === "more_than_one" || i.kind === "no_type";
  const cands = useQuery(i.kind === "more_than_one" ? `import:cands:${i.id}` : null, () => db.select<{ id: string; full_name: string; is_client: boolean }>("people", { id: `in.(${(i.diffs?.candidates ?? []).join(",")})`, select: "id,full_name,is_client" }));
  const found = useQuery(pick && i.kind !== "no_type" && dq.length >= 2 ? `import:find:${dq}` : null, () => db.select<{ id: string; full_name: string }>("people", { full_name: ilike(dq), deleted_at: "is.null", select: "id,full_name", limit: 6 }));
  const r = i.row_data ?? {};
  async function act(action: string) {
    setErr("");
    try {
      await db.rpc("solutions_import_resolve", { p_issue: i.id, p_action: action, p_person: person?.id ?? null, p_type: type || null });
      invalidate("import:"); invalidate("clients"); toast(action === "use_file" ? "Done" : "Kept as it is");
    } catch (e) { setErr((e as Error).message); }
  }
  return (
    <div className="ptype">
      <div className="sol-head"><strong>{KIND[i.kind]}</strong>
        <span className="small muted">{i.import_batches?.provider} · {i.import_batches?.file_name ?? "file"} · {fmtDate(i.import_batches?.created_at)}{r.row ? ` · row ${r.row}` : ""}</span></div>
      <div className="small" style={{ margin: "4px 0 8px" }}>
        {i.people?.full_name && <button className="linkish" onClick={() => navigate(`/clients/${i.person_id}?tab=solutions`)}><b>{i.people.full_name}</b></button>}
        {i.client_holdings && <> · {i.client_holdings.solution_type} · {i.client_holdings.provider} <span className="mono">{i.client_holdings.contract_no}</span> · {i.client_holdings.status}</>}
        {!i.client_holdings && r.client && <> File says: <b>{r.client}</b>{r.contract_no ? <> · <span className="mono">{r.contract_no}</span></> : ""}{r.product ? ` · ${r.product}` : ""}{r.solution_type ? ` · ${r.solution_type}` : ""}{r.market_value ? ` · ${money(Number(r.market_value))}` : ""}</>}
      </div>
      {i.kind === "different" && <table className="data small"><thead><tr><th /><th>CRM</th><th>File</th></tr></thead>
        <tbody>{Object.entries(i.diffs ?? {}).map(([k, v]: any) => <tr key={k}><td>{FIELD_LABEL[k] ?? k}</td><td>{v.crm ?? "—"}</td><td><b>{v.file ?? "—"}</b></td></tr>)}</tbody></table>}
      {pick && (
        <div className="form-grid" style={{ marginTop: 8 }}>
          {i.kind === "more_than_one" && <div className="wide toolbar" style={{ margin: 0 }}><span className="small muted">Which one?</span>
            {(cands.data ?? []).map((c) => <button key={c.id} className={"btn small" + (person?.id === c.id ? " primary" : "")} onClick={() => setPerson(c)}>{c.full_name}{c.is_client ? " (client)" : ""}</button>)}</div>}
          {i.kind === "not_found" && <Field label="Whose is it? (search)">
            {person ? <div><b>{person.full_name}</b> <button className="btn small ghost" onClick={() => setPerson(null)}>Change</button></div>
              : <><input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name" />
                {(found.data ?? []).map((p) => <button key={p.id} className="btn ghost small" style={{ justifyContent: "flex-start" }} onClick={() => setPerson(p)}>{p.full_name}</button>)}</>}
          </Field>}
          {(i.kind === "no_type" || !(r.solution_type || r.product)) && <Field label="Kind of solution"><Select value={type} onChange={setType} options={ref.data?.list("SolutionTypes") ?? []} /></Field>}
        </div>
      )}
      <div className="toolbar" style={{ margin: "8px 0 0" }}>
        {i.kind === "different" && <><button className="btn small primary" onClick={() => act("use_file")}>Use the file</button><button className="btn small" onClick={() => act("keep_crm")}>Keep the CRM</button></>}
        {i.kind === "missing" && <><button className="btn small" onClick={() => act("use_file")}>Mark as Terminated</button><button className="btn small primary" onClick={() => act("keep_crm")}>Keep it</button></>}
        {pick && <><button className="btn small primary" disabled={i.kind !== "no_type" && !person} onClick={() => act("use_file")}>Add {person ? `to ${person.full_name}` : "to this person"}</button>
          <button className="btn small ghost" onClick={() => act("skip")}>Skip this row</button></>}
        {err && <span className="small" style={{ color: "var(--alert)" }}>{err}</span>}
      </div>
    </div>
  );
}

function PastImports() {
  const q = useQuery("import:batches", () => db.select<any>("import_batches", { select: "*", order: "created_at.desc", limit: 10 }));
  if (!q.data?.length) return null;
  return (
    <Panel title="Past imports">
      <div className="table-wrap"><table className="data">
        <thead><tr><th>When</th><th>Provider · file</th><th className="num">Rows</th><th className="num">Added</th><th className="num">Values updated</th><th className="num">Same</th><th className="num">To review</th></tr></thead>
        <tbody>{q.data.map((b: any) => <tr key={b.id}><td className="nowrap">{fmtDate(b.created_at)}</td><td>{b.provider}<div className="small muted">{b.file_name}{b.full_list ? " · full list" : ""}</div></td>
          <td className="num">{b.rows}</td><td className="num">{b.added}</td><td className="num">{b.values_updated}</td><td className="num">{b.same}</td><td className="num">{b.issues}</td></tr>)}</tbody>
      </table></div>
    </Panel>
  );
}
